import {
  ChatScreenData,
  ChatScreenColumn,
  ChatScreenGridData,
  ChatScreenDataRow,
  ChatSearchFormData,
  ChatSearchFormValue,
} from "@shared/type/CommonType";

const CHAT_ROW_LIMIT = 1000;
const CHAT_FIELD_LIMIT = 40;
const CHAT_TEXT_LIMIT = 120;
const CHAT_INTERNAL_FIELDS = new Set([
  "KeyField",
  "block",
  "fixCheckbox",
  "state",
  "__upload_error_summary",
]);

type ChatGridLike = {
  getSelectedRows?: () => unknown;
  getFilteredData?: () => unknown;
  getRowData?: () => unknown;
  getDisplayedRowCount?: () => number;
  getChatFilters?: () => ChatSearchFormData | undefined;
  getAllDisplayedColumns?: () => ChatGridColumnLike[];
  focusedRowNode?: () => { data?: unknown } | null | undefined;
  forEachNode?: (callback: (node: ChatGridRowNodeLike) => void) => void;
  forEachFilteredNode?: (callback: (node: ChatGridRowNodeLike) => void) => void;
};

type ChatGridRowNodeLike = {
  readonly data?: unknown;
  readonly group?: boolean;
  readonly rowPinned?: string | null;
};

type ChatGridColumnLike = {
  getColDef?: () => ChatGridColumnDef | undefined;
};

type ChatGridColumnDef = {
  field?: string;
  colId?: string;
  columnConfig?: {
    cd_column?: string;
    nm_column?: string | null;
    cd_displaycolumn?: string | null;
    cd_grid?: string | null;
    fg_visible?: string;
    tp_column?: string | null;
    cd_clas?: string | null;
    js_combo?: string | null;
  };
};

type GridRefEntry = {
  current?: ChatGridLike | null;
};

type GridRefMap = {
  current?: Record<string, GridRefEntry | ChatGridLike | null>;
};

type CleanChatScreenDataOptions = {
  /**
   * 절단·표시컬럼과 무관하게 행에 항상 실어야 하는 필드(행추가 PK 등, 2026-09-01).
   * 우선순위 -1로 40필드 절단에서 가장 먼저 살아남는다.
   */
  pinnedFields?: string[];
  maxSelectedRows?: number;
  maxVisibleRows?: number;
  maxFields?: number;
  maxTextLength?: number;
};

type NamedChatGrid = {
  id: string;
  grid: ChatGridLike;
};

type ChatLayoutNode = {
  type?: string;
  className?: string;
  style?: { display?: string };
  props?: Readonly<Record<string, unknown>>;
  header?: readonly ChatLayoutNode[];
  body?: readonly ChatLayoutNode[];
  children?: readonly ChatLayoutNode[];
};

export function getVisibleChatGridIds(
  configs: readonly ChatLayoutNode[]
): ReadonlySet<string> | undefined {
  const ids = new Set<string>();
  let hasGrid = false;
  const collect = (config: ChatLayoutNode, hidden: boolean) => {
    const isHidden =
      hidden ||
      config.className?.split(/\s+/).includes("hidden") === true ||
      config.style?.display === "none";
    const refKey = config.props?.["ref_key"];
    hasGrid ||= config.type === "SnGrid";
    if (!isHidden && config.type === "SnGrid" && typeof refKey === "string") {
      ids.add(refKey);
    }
    [...(config.header ?? []), ...(config.body ?? []), ...(config.children ?? [])].forEach(
      (child) => collect(child, isHidden)
    );
  };

  configs.forEach((config) => collect(config, false));
  return hasGrid ? ids : undefined;
}

/** 스냅샷 생성 옵션 — includeFields: 표시 컬럼이 아니어도 원본 행에서 복사할 필드(숨김 PK 등). */
export interface CreateChatScreenDataOptions {
  includeFields?: string[];
}

export function createChatScreenDataFromGrid(
  grid: ChatGridLike,
  options: CreateChatScreenDataOptions = {}
): ChatScreenData | undefined {
  const includeFields = safeIncludeFields(options.includeFields);
  const visibleColumns = chatColumns(grid);
  const rowCount = chatGridRowCount(grid);
  const allRows = getAllRows(grid);
  /* rows 와 같은 범위(필터 전 전체)의 캡 이전 건수 — GridV2 자동 수집도 이 길이다 (2026-10-16 리뷰) */
  const rowsTotal = Array.isArray(allRows) ? allRows.length : undefined;
  return cleanChatScreenData(
    {
      rows: normalizeUnknownRows(chatRows(grid, allRows, includeFields)) as ChatScreenDataRow[],
      selectedRows: normalizeUnknownRows(chatRows(grid, getSelectedRows(grid), includeFields)) as ChatScreenDataRow[],
      visibleRows: normalizeUnknownRows(chatRows(grid, getVisibleRows(grid), includeFields)) as ChatScreenDataRow[],
      visibleColumns,
      filters: grid.getChatFilters?.(),
      ...(rowCount !== undefined ? { rowCount } : {}),
      ...(rowsTotal !== undefined ? { rowsTotal } : {}),
    },
    { pinnedFields: includeFields }
  );
}

function safeIncludeFields(fields: string[] | undefined): string[] {
  return (fields || []).filter((field) => typeof field === "string" && safeFieldName(field));
}

export function createChatScreenDataFromGridRefs(
  gridRefs: GridRefMap | undefined,
  includedGridIds?: ReadonlySet<string>,
  options: CreateChatScreenDataOptions = {}
): ChatScreenData | undefined {
  const includeFields = safeIncludeFields(options.includeFields);
  const grids = Object.entries(gridRefs?.current || {})
    .filter(([id]) => !includedGridIds || includedGridIds.has(id))
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([id, ref]) => {
      const grid = resolveGrid(ref);
      return grid ? { id, grid } : null;
    })
    .filter((grid): grid is NamedChatGrid => grid !== null);

  const screenGrids = Object.fromEntries(
    grids
      .map(({ id, grid }, index) => [
        id,
        createChatScreenGridData(grid, id || `grid${index + 1}`, includeFields),
      ])
      .filter((entry): entry is [string, ChatScreenGridData] => Boolean(entry[1]))
  );
  const gridData = Object.values(screenGrids);
  const selectedRows = firstGridRows(gridData, "selectedRows");
  const visibleGrid = gridData.find(
    (grid) => (grid.visibleRows?.length ?? 0) > 0
  );
  const visibleRows = visibleGrid?.visibleRows ?? [];
  const visibleColumns = gridData.flatMap((grid) => grid.visibleColumns || []);
  const rowCount =
    visibleGrid?.rowCount ??
    gridData.find((grid) => grid.rowCount !== undefined)?.rowCount;

  return cleanChatScreenData({
    ...(selectedRows.length > 0 ? { selectedRows } : {}),
    ...(visibleRows.length > 0 ? { visibleRows } : {}),
    ...(visibleColumns.length > 0 ? { visibleColumns } : {}),
    ...(rowCount !== undefined ? { rowCount } : {}),
    ...(Object.keys(screenGrids).length > 0 ? { grids: screenGrids } : {}),
  }, { pinnedFields: includeFields });
}

export function cleanChatScreenData(
  screenData: ChatScreenData | undefined,
  options: CleanChatScreenDataOptions = {}
): ChatScreenData | undefined {
  if (!screenData) {
    return undefined;
  }
  const {
    maxSelectedRows = CHAT_ROW_LIMIT,
    maxVisibleRows = CHAT_ROW_LIMIT,
    maxFields = CHAT_FIELD_LIMIT,
    maxTextLength = CHAT_TEXT_LIMIT,
  } = options;
  const pinnedFields = new Set(safeIncludeFields(options.pinnedFields));
  const visibleColumns = cleanColumns(screenData.visibleColumns, maxTextLength);
  const filters = cleanFilterData(screenData.filters, maxTextLength);
  const priorityFields = metadataFieldPriority(visibleColumns);
  const grids = cleanGrids(screenData.grids, maxSelectedRows, maxVisibleRows, maxFields, maxTextLength, pinnedFields);
  const rows = normalizeRows(
    screenData.rows,
    priorityFields,
    maxVisibleRows,
    maxFields,
    maxTextLength,
    pinnedFields
  );
  const selectedRows = normalizeRows(
    screenData.selectedRows,
    priorityFields,
    maxSelectedRows,
    maxFields,
    maxTextLength,
    pinnedFields
  );
  const visibleRows = normalizeRows(
    screenData.visibleRows,
    priorityFields,
    maxVisibleRows,
    maxFields,
    maxTextLength,
    pinnedFields
  );
  const hasGrids = Object.keys(grids).length > 0;
  if (
    rows.length === 0 &&
    selectedRows.length === 0 &&
    visibleRows.length === 0 &&
    !hasGrids &&
    visibleColumns.length === 0
  ) {
    return undefined;
  }
  return {
    ...(rows.length > 0 ? { rows } : {}),
    ...(selectedRows.length > 0 ? { selectedRows } : {}),
    ...(visibleRows.length > 0 ? { visibleRows } : {}),
    ...(visibleColumns.length > 0 ? { visibleColumns } : {}),
    ...(Object.keys(filters).length > 0 ? { filters } : {}),
    ...(screenData.rowCount !== undefined ? { rowCount: screenData.rowCount } : {}),
    ...(screenData.rowsTotal !== undefined ? { rowsTotal: screenData.rowsTotal } : {}),
    ...(hasGrids ? { grids } : {}),
  };
}

function createChatScreenGridData(
  grid: ChatGridLike,
  gridId: string,
  includeFields: string[] = []
): ChatScreenGridData | undefined {
  const visibleColumns = chatColumns(grid, gridId);
  const rowCount = chatGridRowCount(grid);
  const allRows = getAllRows(grid);
  /* rows 와 같은 범위(필터 전 전체)의 캡 이전 건수 — rowCount(필터 후)와 섞지 않는다 (2026-10-16) */
  const rowsTotal = Array.isArray(allRows) ? allRows.length : undefined;
  return cleanChatScreenGridData(
    {
      rows: chatRows(grid, allRows, includeFields) as ChatScreenDataRow[],
      selectedRows: chatRows(grid, getSelectedRows(grid), includeFields) as ChatScreenDataRow[],
      visibleRows: chatRows(grid, getVisibleRows(grid), includeFields) as ChatScreenDataRow[],
      visibleColumns,
      filters: grid.getChatFilters?.(),
      ...(rowCount !== undefined ? { rowCount } : {}),
      ...(rowsTotal !== undefined ? { rowsTotal } : {}),
    },
    CHAT_ROW_LIMIT,
    CHAT_ROW_LIMIT,
    CHAT_FIELD_LIMIT,
    CHAT_TEXT_LIMIT,
    new Set(includeFields)
  );
}

function resolveGrid(
  ref: GridRefEntry | ChatGridLike | null
): ChatGridLike | null {
  if (!ref) {
    return null;
  }
  if (isGridRefEntry(ref)) {
    return ref.current || null;
  }
  return ref;
}

function isGridRefEntry(ref: GridRefEntry | ChatGridLike): ref is GridRefEntry {
  return Object.prototype.hasOwnProperty.call(ref, "current");
}

function firstGridRows(
  grids: ChatScreenGridData[],
  key: "selectedRows" | "visibleRows"
): ChatScreenDataRow[] {
  for (const grid of grids) {
    const rows = grid[key];
    if (rows && rows.length > 0) {
      return rows;
    }
  }
  return [];
}

function getSelectedRows(grid: ChatGridLike): unknown {
  const selectedRows = grid.getSelectedRows?.();
  if (Array.isArray(selectedRows) && selectedRows.length > 0) {
    return selectedRows;
  }
  return rowNodeData(grid.focusedRowNode?.());
}

function getVisibleRows(grid: ChatGridLike): unknown {
  const filteredRows = grid.getFilteredData?.();
  if (Array.isArray(filteredRows) && filteredRows.length > 0) {
    return filteredRows;
  }

  const rowData = grid.getRowData?.();
  if (Array.isArray(rowData) && rowData.length > 0) {
    return rowData;
  }

  if (typeof grid.forEachFilteredNode === "function") {
    const rows: unknown[] = [];
    grid.forEachFilteredNode((node) => {
      if (node?.data) {
        rows.push(node.data);
      }
    });
    return rows;
  }

  return [];
}

function chatGridRowCount(grid: ChatGridLike): number | undefined {
  if (typeof grid.forEachFilteredNode === "function") {
    let rowCount = 0;
    grid.forEachFilteredNode((node) => {
      if (
        !node.group &&
        !node.rowPinned &&
        node.data !== undefined &&
        node.data !== null
      ) {
        rowCount += 1;
      }
    });
    return rowCount;
  }
  const displayedRowCount = grid.getDisplayedRowCount?.();
  return typeof displayedRowCount === "number" &&
    Number.isFinite(displayedRowCount)
    ? Math.max(0, Math.trunc(displayedRowCount))
    : undefined;
}

function getAllRows(grid: ChatGridLike): unknown {
  const rowData = grid.getRowData?.();
  if (Array.isArray(rowData) && rowData.length > 0) {
    return rowData;
  }

  if (typeof grid.forEachNode === "function") {
    const rows: unknown[] = [];
    grid.forEachNode((node) => {
      if (node?.data) {
        rows.push(node.data);
      }
    });
    return rows;
  }

  return getVisibleRows(grid);
}

function rowNodeData(rowNode: { data?: unknown } | null | undefined) {
  return rowNode?.data ? [rowNode.data] : [];
}

function normalizeUnknownRows(rows: unknown): unknown[] {
  return Array.isArray(rows) ? rows : [];
}

function chatRows(
  grid: ChatGridLike,
  rows: unknown,
  includeFields: string[] = []
): unknown[] {
  const filterFields = new Set(Object.keys(grid.getChatFilters?.() || {}));
  return normalizeUnknownRows(rows).map((row) =>
    chatRow(grid, row, filterFields, includeFields)
  );
}

function chatRow(
  grid: ChatGridLike,
  row: unknown,
  filterFields: Set<string>,
  includeFields: string[] = []
): unknown {
  if (!row || typeof row !== "object" || Array.isArray(row)) {
    return row;
  }
  const source = row as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  // 숨김 PK 등 includeFields는 표시 컬럼과 무관하게 원본에서 먼저 복사한다(2026-09-01, copyByKey).
  includeFields.forEach((field) => {
    if (field in source && !(field in result)) {
      result[field] = source[field];
    }
  });

  for (const column of visibleColumnDefs(grid)) {
    const config = column.columnConfig;
    const field = config?.cd_column || column.field || column.colId;
    if (!field || !(field in source)) {
      continue;
    }
    result[field] = source[field];

    const displayField = config?.cd_displaycolumn || displayFieldFor(field);
    if (config?.cd_displaycolumn && config.cd_displaycolumn in source) {
      result[config.cd_displaycolumn] = source[config.cd_displaycolumn];
    }

    const comboName = comboDisplayName(config, source[field]);
    if (comboName !== null && comboName !== String(source[field])) {
      result[displayField] = comboName;
    }
  }
  copyFilterFields(source, result, filterFields);

  return result;
}

function copyFilterFields(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  filterFields: Set<string>
): void {
  filterFields.forEach((field) => {
    if (field in target || !safeFieldName(field)) {
      return;
    }
    const value = source[field];
    const normalized = normalizeValue(value);
    if (normalized !== undefined && normalized !== null && normalized !== "") {
      target[field] = normalized;
    }
  });
}

function chatColumns(grid: ChatGridLike, fallbackGridId?: string): ChatScreenColumn[] {
  return visibleColumnDefs(grid)
    .map((column) => chatColumnMetadata(column, fallbackGridId))
    .filter((column): column is ChatScreenColumn => column !== null);
}

function chatColumnMetadata(
  column: ChatGridColumnDef,
  fallbackGridId?: string
): ChatScreenColumn | null {
  const config = column.columnConfig;
  const field = config?.cd_column || column.field || column.colId;
  const label = config?.nm_column?.trim();
  if (!field || !label) {
    return null;
  }
  const displayField = config?.cd_displaycolumn || displayFieldFor(field);
  return {
    field,
    label,
    visible: true,
    ...(config?.tp_column ? { type: config.tp_column } : {}),
    ...(displayField ? { displayField } : {}),
    ...(config?.cd_clas ? { codeClass: config.cd_clas } : {}),
    ...(config?.cd_grid || fallbackGridId
      ? { gridId: config?.cd_grid || fallbackGridId }
      : {}),
  };
}

function visibleColumnDefs(grid: ChatGridLike): ChatGridColumnDef[] {
  return (grid.getAllDisplayedColumns?.() || [])
    .map((column) => column.getColDef?.())
    .filter(
      (column): column is ChatGridColumnDef =>
        Boolean(column) && column?.columnConfig?.fg_visible === "Y"
    );
}

function comboDisplayName(
  config: ChatGridColumnDef["columnConfig"],
  value: unknown
): string | null {
  if (config?.tp_column !== "SnComboBox") {
    return null;
  }
  const source = config.js_combo;
  if (!source || value === null || value === undefined) {
    return null;
  }
  const parsed = parseComboList(source);
  if (!parsed) {
    return null;
  }
  const list = parsed;
  const found = list.find((item) => String(item.code) === String(value));
  return found?.name === undefined ? null : String(found.name);
}

function parseComboList(
  source: string
): Array<{ code?: unknown; name?: unknown }> | null {
  try {
    const parsed = JSON.parse(source);
    return Array.isArray(parsed) ? parsed.filter(isComboOption) : null;
  } catch (error) {
    if (error instanceof SyntaxError) {
      return null;
    }
    throw error;
  }
}

function isComboOption(
  value: unknown
): value is { code?: unknown; name?: unknown } {
  return Boolean(value) && typeof value === "object";
}

function displayFieldFor(field: string): string {
  if (field.startsWith("cd_")) {
    return `nm_${field.substring("cd_".length)}`;
  }
  return `nm_${field}`;
}

function cleanColumns(
  columns: ChatScreenColumn[] | undefined,
  maxTextLength: number
): ChatScreenColumn[] {
  if (!Array.isArray(columns)) {
    return [];
  }
  return columns
    .filter((column) => column.visible && column.field.trim() && column.label.trim())
    .map((column) => ({
      field: column.field.trim(),
      label: truncateText(column.label.trim(), maxTextLength),
      visible: true,
      ...(column.type
        ? { type: truncateText(column.type.trim(), maxTextLength) }
        : {}),
      ...(column.displayField
        ? { displayField: truncateText(column.displayField.trim(), maxTextLength) }
        : {}),
      ...(column.codeClass
        ? { codeClass: truncateText(column.codeClass.trim(), maxTextLength) }
        : {}),
      ...(column.gridId
        ? { gridId: truncateText(column.gridId.trim(), maxTextLength) }
        : {}),
    }));
}

function cleanGrids(
  grids: Record<string, ChatScreenGridData | undefined> | undefined,
  maxSelectedRows: number,
  maxVisibleRows: number,
  maxFields: number,
  maxTextLength: number,
  pinnedFields: Set<string> = new Set()
): Record<string, ChatScreenGridData> {
  const result: Record<string, ChatScreenGridData> = {};
  Object.entries(grids || {}).forEach(([gridId, gridData]) => {
    const cleaned = cleanChatScreenGridData(
      gridData,
      maxSelectedRows,
      maxVisibleRows,
      maxFields,
      maxTextLength,
      pinnedFields
    );
    if (gridId.trim() && cleaned) {
      result[gridId] = cleaned;
    }
  });
  return result;
}

function cleanChatScreenGridData(
  gridData: ChatScreenGridData | undefined,
  maxSelectedRows: number,
  maxVisibleRows: number,
  maxFields: number,
  maxTextLength: number,
  pinnedFields: Set<string> = new Set()
): ChatScreenGridData | undefined {
  if (!gridData) {
    return undefined;
  }
  const visibleColumns = cleanColumns(gridData.visibleColumns, maxTextLength);
  const priorityFields = metadataFieldPriority(visibleColumns);
  const rows = normalizeRows(
    gridData.rows,
    priorityFields,
    maxVisibleRows,
    maxFields,
    maxTextLength,
    pinnedFields
  );
  const selectedRows = normalizeRows(
    gridData.selectedRows,
    priorityFields,
    maxSelectedRows,
    maxFields,
    maxTextLength,
    pinnedFields
  );
  const visibleRows = normalizeRows(
    gridData.visibleRows,
    priorityFields,
    maxVisibleRows,
    maxFields,
    maxTextLength,
    pinnedFields
  );
  const filters = cleanFilterData(gridData.filters, maxTextLength);
  // 조회 전(row 없음)에도 컬럼 메타는 보낸다. /도움의 차트/집계 예시 생성에 필요하다.
  if (
    rows.length === 0 &&
    selectedRows.length === 0 &&
    visibleRows.length === 0 &&
    visibleColumns.length === 0
  ) {
    return undefined;
  }
  return {
    ...(rows.length > 0 ? { rows } : {}),
    ...(selectedRows.length > 0 ? { selectedRows } : {}),
    ...(visibleRows.length > 0 ? { visibleRows } : {}),
    ...(visibleColumns.length > 0 ? { visibleColumns } : {}),
    ...(Object.keys(filters).length > 0 ? { filters } : {}),
    ...(gridData.rowCount !== undefined ? { rowCount: gridData.rowCount } : {}),
    ...(gridData.rowsTotal !== undefined ? { rowsTotal: gridData.rowsTotal } : {}),
  };
}

function cleanFilterData(
  filters: ChatSearchFormData | undefined,
  maxTextLength: number
): ChatSearchFormData {
  const result: ChatSearchFormData = {};
  Object.entries(filters || {}).forEach(([field, value]) => {
    if (!safeFieldName(field)) {
      return;
    }
    const normalized = normalizeValue(value);
    if (normalized !== undefined && normalized !== null && normalized !== "") {
      result[field] =
        typeof normalized === "string"
          ? truncateText(normalized, maxTextLength)
          : normalized;
    }
  });
  return result;
}

function metadataFieldPriority(columns: ChatScreenColumn[]): Set<string> {
  const fields = new Set<string>();
  columns.forEach((column) => {
    fields.add(column.field);
    if (column.displayField) {
      fields.add(column.displayField);
    }
  });
  return fields;
}

function normalizeRows(
  rows: unknown,
  priorityFields: Set<string>,
  maxRows: number,
  maxFields: number,
  maxTextLength: number,
  pinnedFields: Set<string> = new Set()
): ChatScreenDataRow[] {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows
    .slice(0, maxRows)
    .map((row) => normalizeRow(row, priorityFields, maxFields, maxTextLength, pinnedFields))
    .filter((row): row is ChatScreenDataRow => row !== null);
}

function safeFieldName(field: string): boolean {
  return /^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(field);
}

function normalizeRow(
  row: unknown,
  priorityFields: Set<string>,
  maxFields: number,
  maxTextLength: number,
  pinnedFields: Set<string> = new Set()
): ChatScreenDataRow | null {
  if (!row || typeof row !== "object" || Array.isArray(row)) {
    return null;
  }
  const result: ChatScreenDataRow = {};
  sortedRowEntries(row, priorityFields, pinnedFields).some(([key, value]) => {
    if (Object.keys(result).length >= maxFields) {
      return true;
    }
    if (CHAT_INTERNAL_FIELDS.has(key) || key.startsWith("__")) {
      return false;
    }
    const normalized = normalizeValue(value);
    if (normalized !== undefined && normalized !== null && normalized !== "") {
      result[key] =
        typeof normalized === "string"
          ? truncateText(normalized, maxTextLength)
          : normalized;
    }
    return false;
  });
  return Object.keys(result).length > 0 ? result : null;
}

function sortedRowEntries(
  row: object,
  priorityFields: Set<string>,
  pinnedFields: Set<string> = new Set()
): [string, unknown][] {
  return Object.entries(row as Record<string, unknown>).sort(
    ([left], [right]) =>
      fieldPriority(left, priorityFields, pinnedFields) -
      fieldPriority(right, priorityFields, pinnedFields)
  );
}

function fieldPriority(
  field: string,
  priorityFields: Set<string>,
  pinnedFields: Set<string> = new Set()
): number {
  if (pinnedFields.has(field)) {
    return -1; // 행추가 PK 등 — 표시 컬럼(0)보다 먼저 살아남는다(40필드 절단 보호)
  }
  if (priorityFields.has(field)) {
    return 0;
  }
  if (field.startsWith("nm_")) {
    return 1;
  }
  if (
    field.startsWith("no_") ||
    field.startsWith("dt_") ||
    field.startsWith("cd_") ||
    field.startsWith("st_")
  ) {
    return 2;
  }
  return 3;
}

function truncateText(value: string, maxLength: number): string {
  return value.length > maxLength ? value.slice(0, maxLength) : value;
}

function normalizeValue(value: unknown): ChatSearchFormValue {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null ||
    value === undefined
  ) {
    return value;
  }
  return undefined;
}
